import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Alert,
    CircularProgress,
    Card,
    CardMedia,
    Divider,
    Grid,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Link,
    DialogActions,
    Button,
    DialogContent,
    Dialog,
    DialogTitle
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FlightIcon from '@mui/icons-material/Flight';
import HotelIcon from '@mui/icons-material/Hotel';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import ReceiptIcon from '@mui/icons-material/Receipt';

import { PAContext } from "../../Services/PAContext";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";

// Helper function to extract a string key safely from receipt object variations
const getReceiptKey = (receipt, fallbackIndex) => {
    if (!receipt) return `receipt-${fallbackIndex}`;

    // Explicit string IDs
    if (typeof receipt.id === 'string' && receipt.id) return receipt.id;

    // Handle Mongo ObjectIDs or nested _id shapes
    if (receipt._id) {
        if (typeof receipt._id === 'string') return receipt._id;
        if (receipt._id.$oid && typeof receipt._id.$oid === 'string') return receipt._id.$oid;
        if (receipt._id.timestamp) return `receipt-${receipt._id.timestamp}-${fallbackIndex}`;
    }

    return `receipt-${fallbackIndex}`;
};

const ExpenseReviewPage = () => {

    const { dataArray, sendSocketMessage, removeFirstBusinessExpenseReportActions } = useContext(PAContext);
    const { BusinessExpenseReportPipeline } = dataArray;

    const [searchParams] = useSearchParams();

    const eventId = searchParams.get('eventId');
    const attendeeId = searchParams.get('attendeeId');
    const reviewerEmail = searchParams.get('email');
    const token = searchParams.get('token');

    const [loading, setLoading] = useState(true);
    const [reportData, setReportData] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");

    // Action Loading & Local Input States
    const [sendingEmail, setSendingEmail] = useState(false);
    const [revokingToken, setRevokingToken] = useState(false);
    const [postingMessage, setPostingMessage] = useState(false);
    const [newMessageText, setNewMessageText] = useState("");

    // Receipt GCS Signed URL State Management
    const [processedReceipts, setProcessedReceipts] = useState([]);
    const [previewModal, setPreviewModal] = useState({ open: false, url: '', title: '' });

    // 1. Dispatch WebSocket message once on mount
    useEffect(() => {
        if (token && eventId) {
            sendSocketMessage({
                action: "E_getEventExpenseReportDetailsForReviewer",
                event_id: eventId,
                attendee_id: attendeeId,
                reviewer_email: reviewerEmail,
                token: token
            });
        }
    }, [token, eventId, attendeeId, reviewerEmail]);

    // 2. Process incoming pipeline data
    useEffect(() => {
        if (!BusinessExpenseReportPipeline || BusinessExpenseReportPipeline.length === 0) return;

        const _incoming = BusinessExpenseReportPipeline[0];

        console.log("*********** 555 ********* ", _incoming);

        const done = () => {
            removeFirstBusinessExpenseReportActions();
        };

        if (_incoming.action === "E_getEventExpenseReportDetailsForReviewerResults") {
            setLoading(false);
            if (_incoming.result === "ok") {
                setReportData(_incoming.event_data);
            } else {
                setErrorMessage(_incoming.reason || "Failed to load expense report details.");
            }
            done();
            return;
        }

        // Handle incoming batch signed URLs response
        if (_incoming.action === "E_getBatchSignedUrlsForGCSForEventExpenseReportResults") {
            if (_incoming.result === "ok" && Array.isArray(_incoming.signed_photos)) {
                const urlMap = new Map();
                _incoming.signed_photos.forEach(item => {
                    const idKey = item.photo_id;
                    if (idKey && item.signed_url) {
                        urlMap.set(idKey, item.signed_url);
                    }
                });

                setProcessedReceipts(prev => prev.map(rcpt => {
                    const rcptKey = rcpt.id || rcpt.temp_id || rcpt._id;
                    if (urlMap.has(rcptKey)) {
                        return { ...rcpt, displaySrc: urlMap.get(rcptKey), isPendingUrl: false };
                    }
                    return rcpt;
                }));
            }
            done();
            return;
        }

        // If no action matched, done() is NOT called and the item remains in the pipeline array.

    }, [BusinessExpenseReportPipeline]);

    // 3. Evaluate & process GCS paths whenever reportData.receipts changes
    useEffect(() => {
        const rawReceipts = reportData?.receipts || [];
        if (rawReceipts.length === 0) {
            setProcessedReceipts([]);
            return;
        }

        const missingUrlsBatch = [];

        const updated = rawReceipts.map((item, idx) => {
            const rawUrl = item.full_data || item.file_url || item.url || item.gcs_path || item.path || '';
            const isLocal = typeof rawUrl === 'string' && (rawUrl.startsWith('blob:') || rawUrl.startsWith('data:'));

            if (isLocal) {
                return { ...item, displaySrc: rawUrl };
            }

            if (item.signed_url) {
                return { ...item, displaySrc: item.signed_url };
            }

            // Detect Google Cloud Storage path requirement
            const needsGCS = item.gcs_path || (typeof rawUrl === 'string' && (rawUrl.includes('storage.googleapis.com') || rawUrl.startsWith('gs://') || (rawUrl && !rawUrl.startsWith('http'))));

            if (needsGCS) {
                const photoId = item.id || item.temp_id || getReceiptKey(item, idx);
                missingUrlsBatch.push({
                    photo_id: photoId,
                    temp_id: item.temp_id || photoId,
                    gcs_path: item.gcs_path || rawUrl
                });
                return { ...item, displaySrc: null, isPendingUrl: true };
            }

            return { ...item, displaySrc: rawUrl };
        });

        if (missingUrlsBatch.length > 0 && eventId) {
            sendSocketMessage({
                action: "E_getBatchSignedUrlsForGCSForEventExpenseReport",
                event_id: eventId,
                photos: missingUrlsBatch,
                is_thumbnail: false
            });
        }

        setProcessedReceipts(updated);
    }, [reportData?.receipts, eventId]);

    // Validation Guard
    if (!token || !eventId || !attendeeId) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Alert severity="error">
                    Invalid or incomplete review access link. Please check your link or request a new one.
                </Alert>
            </Container>
        );
    }

    // Placeholder Loading State
    if (loading) {
        return (
            <Container maxWidth="md" sx={{ mt: 8, textAlign: 'center' }}>
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
                    Gathering expense report details...
                </Typography>
            </Container>
        );
    }

    // Error State
    if (errorMessage) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Alert severity="error">{errorMessage}</Alert>
            </Container>
        );
    }

    const event_details = reportData?.event_details || {};
    const attendee_details = reportData?.attendee_details || {};
    const lodging = reportData?.lodging || [];
    const activities = reportData?.activities || [];
    const meals = reportData?.meals || [];
    const flights = reportData?.flights || [];
    const receipts = reportData?.receipts || [];

    // Use processedReceipts instead of raw receipts for linked rendering
    const getLinkedReceipts = (itemId, sectionKey) => {
        return processedReceipts.filter(r => {
            const receiptItemId = r.item_id || r.linked_id || r.section_id;
            const matchesId = itemId != null && receiptItemId != null && String(receiptItemId) === String(itemId);
            const matchesSection = r.section && typeof r.section === 'string' && r.section.toLowerCase().includes(sectionKey.toLowerCase());
            return matchesId || matchesSection;
        });
    };

    // Calculate total expense amount across all receipts
    const totalReceiptsAmount = receipts.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

    const handleOpenPreview = (url, title) => {
        setPreviewModal({ open: true, url, title });
    };


    // Helper setter for review tokens inside reportData
    const setReviewTokens = (updater) => {
        setReportData(prev => {
            if (!prev) return prev;
            const currentTokens = prev.review_access_tokens || [];
            const updatedTokens = typeof updater === 'function' ? updater(currentTokens) : updater;
            return { ...prev, review_access_tokens: updatedTokens };
        });
    };

    // Helper setter for message thread inside reportData
    const setMessageThread = (updater) => {
        setReportData(prev => {
            if (!prev) return prev;
            const currentMessages = prev.messages || [];
            const updatedMessages = typeof updater === 'function' ? updater(currentMessages) : updater;
            return { ...prev, messages: updatedMessages };
        });
    };


    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100vw',
                height: '100vh',
                overflowY: 'scroll',
                overflowX: 'hidden',
                bgcolor: '#f4f6f8',
                boxSizing: 'border-box'
            }}
        >
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 8 }}>

                    {/* Header Banner with Small Cover Thumbnail */}
                    <Box mb={3}>
                        <Grid container spacing={2} alignItems="center">
                            {event_details?.cover_image_thumbnail && (
                                <Grid item>
                                    <Card sx={{ width: 120, height: 120, borderRadius: 2, overflow: 'hidden', flexShrink: 0 }}>
                                        <CardMedia
                                            component="img"
                                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            image={event_details.cover_image_thumbnail}
                                            alt={event_details.title || "Event Cover"}
                                        />
                                    </Card>
                                </Grid>
                            )}
                            <Grid item xs={12} sm>
                                <Typography variant="h4" component="h1" fontWeight="bold">
                                    {event_details?.title || "Expense Report Review"}
                                </Typography>
                                <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0.5 }}>
                                    Submitted by: <strong>{attendee_details?.fname} {attendee_details?.lname}</strong> ({attendee_details?.email})
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md="auto" textAlign={{ xs: 'left', md: 'right' }}>
                                <Chip
                                    label={`Reviewer: ${reviewerEmail}`}
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontWeight: '500' }}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Metrics Summary */}
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#f8f9fa' }}>
                                <Typography variant="caption" color="text.secondary">TOTAL EXPENSE CLAIMED</Typography>
                                <Typography variant="h5" color="primary.main" fontWeight="bold">
                                    ${totalReceiptsAmount.toFixed(2)}
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#f8f9fa' }}>
                                <Typography variant="caption" color="text.secondary">RECEIPTS SUBMITTED</Typography>
                                <Typography variant="h5" color="info.main" fontWeight="bold">
                                    {processedReceipts.length}
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#f8f9fa' }}>
                                <Typography variant="caption" color="text.secondary">ITEMS LINKED</Typography>
                                <Typography variant="h5" color="success.main" fontWeight="bold">
                                    {flights.length + lodging.length + meals.length + activities.length}
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#f8f9fa' }}>
                                <Typography variant="caption" color="text.secondary">ATTENDEE RSVP</Typography>
                                <Typography variant="h5" color="secondary.main" fontWeight="bold">
                                    {attendee_details?.rsvp || "N/A"}
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* FLIGHTS SECTION */}
                    <SectionBlock
                        title="Flights"
                        icon={<FlightIcon color="primary" />}
                        items={flights}
                        sectionKey="flight"
                        getLinkedReceipts={getLinkedReceipts}
                        onPreview={handleOpenPreview}
                    />

                    {/* LODGING SECTION */}
                    <SectionBlock
                        title="Lodging"
                        icon={<HotelIcon color="primary" />}
                        items={lodging}
                        sectionKey="lodging"
                        getLinkedReceipts={getLinkedReceipts}
                        onPreview={handleOpenPreview}
                    />

                    {/* MEALS SECTION */}
                    <SectionBlock
                        title="Meals"
                        icon={<RestaurantIcon color="primary" />}
                        items={meals}
                        sectionKey="meal"
                        getLinkedReceipts={getLinkedReceipts}
                        onPreview={handleOpenPreview}
                    />

                    {/* ACTIVITIES SECTION */}
                    <SectionBlock
                        title="Activities"
                        icon={<LocalActivityIcon color="primary" />}
                        items={activities}
                        sectionKey="activit"
                        getLinkedReceipts={getLinkedReceipts}
                        onPreview={handleOpenPreview}
                    />

                    {/* ALL SUBMITTED RECEIPTS OVERVIEW */}
                    <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ReceiptIcon color="action" /> All Business Receipts ({processedReceipts.length})
                    </Typography>

                    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto', mb: 4 }}>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: '#f1f3f5' }}>
                                <TableRow>
                                    <TableCell><strong>Thumbnail</strong></TableCell>
                                    <TableCell><strong>Section</strong></TableCell>
                                    <TableCell><strong>Description / Title</strong></TableCell>
                                    <TableCell align="right"><strong>Amount</strong></TableCell>
                                    <TableCell align="center"><strong>Receipt Action</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {processedReceipts && processedReceipts.length > 0 ? (
                                    processedReceipts.map((receipt, idx) => {
                                        const title = receipt.title || receipt.description || receipt.location?.name || 'Receipt';
                                        return (
                                            <TableRow key={getReceiptKey(receipt, idx)}>
                                                <TableCell sx={{ width: 60 }}>
                                                    {receipt.displaySrc ? (
                                                        <Box
                                                            component="img"
                                                            src={receipt.displaySrc}
                                                            alt={title}
                                                            onClick={() => handleOpenPreview(receipt.displaySrc, title)}
                                                            sx={{
                                                                width: 42,
                                                                height: 42,
                                                                borderRadius: 1,
                                                                objectFit: 'cover',
                                                                cursor: 'pointer',
                                                                border: '1px solid #e0e0e0',
                                                                '&:hover': { opacity: 0.8 }
                                                            }}
                                                        />
                                                    ) : receipt.isPendingUrl ? (
                                                        <CircularProgress size={20} />
                                                    ) : (
                                                        <ReceiptIcon color="disabled" />
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ textTransform: 'capitalize' }}>
                                                    {receipt.section || "General"}
                                                </TableCell>
                                                <TableCell>{title}</TableCell>
                                                <TableCell align="right">
                                                    ${parseFloat(receipt.amount || 0).toFixed(2)}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {receipt.displaySrc ? (
                                                        <Button
                                                            size="small"
                                                            startIcon={<VisibilityIcon />}
                                                            onClick={() => handleOpenPreview(receipt.displaySrc, title)}
                                                        >
                                                            View File
                                                        </Button>
                                                    ) : receipt.isPendingUrl ? (
                                                        <Typography variant="caption" color="text.secondary">Loading Access...</Typography>
                                                    ) : (
                                                        "—"
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            No business receipts uploaded.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                </Paper>

                {/* Receipt Media Preview Lightbox Modal */}
                <Dialog
                    open={previewModal.open}
                    onClose={() => setPreviewModal({ open: false, url: '', title: '' })}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle
                        variant="h6"
                        fontWeight="bold"
                        sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                        {previewModal.title || 'Receipt Preview'}
                        <IconButton onClick={() => setPreviewModal({ open: false, url: '', title: '' })}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers sx={{ textAlign: 'center', p: 2, minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f9fafb' }}>
                        {previewModal.url ? (
                            <Box
                                component="img"
                                src={previewModal.url}
                                alt="Receipt Large Preview"
                                sx={{
                                    maxWidth: '100%',
                                    maxHeight: '70vh',
                                    objectFit: 'contain',
                                    borderRadius: 1,
                                    boxShadow: 2
                                }}
                            />
                        ) : (
                            <Typography color="text.secondary">Unable to render preview.</Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        {previewModal.url && (
                            <Button component={Link} href={previewModal.url} target="_blank" rel="noopener">
                                Open Original File
                            </Button>
                        )}
                        <Button onClick={() => setPreviewModal({ open: false, url: '', title: '' })}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    );
};

// Sub-component to cleanly render each category (Flights, Lodging, Meals, Activities)
const SectionBlock = ({ title, icon, items, sectionKey, getLinkedReceipts, onPreview }) => {

    if (!items || !Array.isArray(items) || items.length === 0) return null;

    // Helper to safely extract string values from nested objects or primitives
    const renderLocation = (loc) => {
        if (!loc) return null;
        if (typeof loc === 'string') return loc;
        if (typeof loc === 'object') return loc.address || loc.name || loc.formatted_address || null;
        return null;
    };

    return (
        <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                {icon} {title} ({items.length})
            </Typography>

            {items.map((item, idx) => {
                const itemId = item.id || item._id;
                const linkedReceipts = getLinkedReceipts(itemId, sectionKey);
                const locationDisplay = renderLocation(item.location) || item.address || item.destination;

                return (
                    <Accordion key={itemId || `section-item-${idx}`} defaultExpanded variant="outlined" sx={{ mb: 1.5 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 2, alignItems: 'center' }}>
                                <Typography fontWeight="600">
                                    {item.title || item.name || item.description || item.airline || item.hotel_name || `${title} Item #${idx + 1}`}
                                </Typography>
                                <Chip
                                    label={`${linkedReceipts.length} Receipt(s)`}
                                    size="small"
                                    color={linkedReceipts.length > 0 ? "success" : "default"}
                                />
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ bgcolor: '#fafafa' }}>
                            <Grid container spacing={2}>
                                {(item.vendor || item.airline || item.hotel_name) && (
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="caption" color="text.secondary">Vendor / Provider</Typography>
                                        <Typography variant="body2">{item.vendor || item.airline || item.hotel_name}</Typography>
                                    </Grid>
                                )}
                                {locationDisplay && (
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="caption" color="text.secondary">Location</Typography>
                                        <Typography variant="body2">{locationDisplay}</Typography>
                                    </Grid>
                                )}
                                {(item.date || item.flight_date || item.check_in_date) && (
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="caption" color="text.secondary">Date</Typography>
                                        <Typography variant="body2">{item.date || item.flight_date || item.check_in_date}</Typography>
                                    </Grid>
                                )}
                            </Grid>

                            {/* Linked Receipts Detail Table */}
                            {linkedReceipts.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                        Attached Receipts:
                                    </Typography>
                                    {linkedReceipts.map((rcpt, rIdx) => {
                                        const rcptTitle = rcpt.title || rcpt.description || rcpt.location?.name || 'Uploaded Receipt';
                                        return (
                                            <Paper key={getReceiptKey(rcpt, rIdx)} variant="outlined" sx={{ p: 1.5, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    {rcpt.displaySrc ? (
                                                        <Box
                                                            component="img"
                                                            src={rcpt.displaySrc}
                                                            alt={rcptTitle}
                                                            onClick={() => onPreview(rcpt.displaySrc, rcptTitle)}
                                                            sx={{ width: 36, height: 36, borderRadius: 1, objectFit: 'cover', cursor: 'pointer', border: '1px solid #ddd' }}
                                                        />
                                                    ) : rcpt.isPendingUrl ? (
                                                        <CircularProgress size={18} />
                                                    ) : null}
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="500">
                                                            {rcptTitle}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Amount: ${parseFloat(rcpt.amount || 0).toFixed(2)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                {rcpt.displaySrc ? (
                                                    <Button
                                                        size="small"
                                                        onClick={() => onPreview(rcpt.displaySrc, rcptTitle)}
                                                    >
                                                        View Attachment
                                                    </Button>
                                                ) : rcpt.isPendingUrl ? (
                                                    <Typography variant="caption" color="text.secondary">Loading Access...</Typography>
                                                ) : null}
                                            </Paper>
                                        );
                                    })}
                                </Box>
                            )}
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );
};

export default ExpenseReviewPage;